import { Formik } from "formik";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft } from "react-icons/bi";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { DATE_FORMAT, INSURANCE_PLAN_RISKS } from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import { formatPricerDate } from "../List/formatPricerDate";
import {
  RATE_LOOKUP_DURATIONS,
  toPricerDateForm,
  toRateLookupForm,
} from "./form";

const RateLookupView = ({
  onSubmit,
  data = {},
  cancel,
  coveragePricers = [],
  onOpenCoverage,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const isEdit = Boolean(data.id);
  const HEADER = t(isEdit ? "DETAIL" : "CREATE", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const [insurancePlans, setInsurancePlans] = useState([]);
  const hasRates = Boolean(get(data, "prices.rates"));

  useQuery(INSURANCE_PLANS, {
    variables: {
      where: { risk: INSURANCE_PLAN_RISKS.SPORTS_LEISURE },
      take: null,
    },
    onCompleted: ({ insurancePlans: { data: plans } }) => {
      setInsurancePlans(
        plans.map(({ id, name }) => ({ value: id, label: name })),
      );
    },
  });

  return (
    <Formik
      initialValues={{
        name: data.name || "",
        insurancePlan: {
          id: get(data, "insurancePlan.id", ""),
        },
        dates: toPricerDateForm(data),
        rates: toRateLookupForm(data.prices),
      }}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, setFieldValue, values, ...formProps }) => (
        <>
          <Header
            title={HEADER.HEADER.TITLE}
            subtitle={HEADER.HEADER.ACTION}
            actions={[
              {
                text: HEADER.HEADER.DISCARD,
                onClick: cancel,
                type: "primary--outlined",
                icon: <BiChevronLeft size={20} />,
              },
              {
                text: HEADER.HEADER.SAVE,
                onClick: handleSubmit,
                type: "primary",
                icon: <BiCheck size={20} />,
              },
            ]}
          />
          <Card title={FORM.GENERAL_INFO}>
            <div className="grid--2">
              <Select
                options={insurancePlans}
                placeholder={FORM.INSURANCE_PLAN}
                label={FORM.INSURANCE_PLAN}
                value={get(values, "insurancePlan.id")}
                {...bindInputProps({
                  name: "insurancePlan.id",
                  values,
                  ...formProps,
                })}
                onChange={(id) => setFieldValue("insurancePlan.id", id || "")}
              />
              <Input
                label={FORM.NAME}
                placeholder={FORM.NAME}
                {...bindInputProps({ name: "name", values, ...formProps })}
              />
              <Input
                type="date"
                variant="range"
                label={FORM.DATE}
                format={DATE_FORMAT}
                placeholders={[FORM.START_DATE, FORM.END_DATE]}
                {...bindInputProps({ name: "dates", values, ...formProps })}
                onChange={(value) => setFieldValue("dates", value)}
              />
            </div>
          </Card>
          {(hasRates || !isEdit) && (
            <>
              <div className="m-top-24" />
              <Card type="simple" head title={FORM.PRICES}>
                <div className="d-flex flex--column gap-16">
                  {RATE_LOOKUP_DURATIONS.map((duration) => (
                    <div className="grid--2" key={duration}>
                      <Input
                        type="number"
                        label={`${FORM.DURATIONS[duration]} — ${FORM.PUBLIC_PRICE}`}
                        {...bindInputProps({
                          name: `rates.${duration}.public`,
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        type="number"
                        label={`${FORM.DURATIONS[duration]} — ${FORM.PARTNER_PRICE}`}
                        {...bindInputProps({
                          name: `rates.${duration}.partner`,
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}
          {!hasRates && isEdit && coveragePricers.length > 0 && (
            <>
              <div className="m-top-24" />
              <Card type="simple" head title={FORM.COMPOSABLE_RATES}>
                <p className="m-bottom-16">{FORM.COMPOSABLE_HINT}</p>
                <div className="d-flex flex--column gap-16">
                  {coveragePricers.map((pricer) => {
                    const rates = toRateLookupForm(pricer.prices);
                    return (
                      <div
                        key={pricer.id}
                        className="d-flex flex--column gap-8"
                      >
                        <strong>{pricer.name}</strong>
                        <span>
                          {FORM.DATE}
                          {": "}
                          {formatPricerDate(pricer.startDate, DATE_FORMAT)}
                          {" — "}
                          {formatPricerDate(pricer.endDate, DATE_FORMAT)}
                        </span>
                        {RATE_LOOKUP_DURATIONS.map((duration) => (
                          <div className="grid--2" key={duration}>
                            <span>
                              {FORM.DURATIONS[duration]} — {FORM.PUBLIC_PRICE}
                              {": "}
                              {get(rates, [duration, "public"], "-")}
                            </span>
                            <span>
                              {FORM.DURATIONS[duration]} — {FORM.PARTNER_PRICE}
                              {": "}
                              {get(rates, [duration, "partner"], "-")}
                            </span>
                          </div>
                        ))}
                        {onOpenCoverage && (
                          <Button
                            type="primary--outlined"
                            onClick={() => onOpenCoverage(pricer)}
                          >
                            {FORM.COMPOSABLE_OPEN_RATES}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            </>
          )}
        </>
      )}
    </Formik>
  );
};

export default RateLookupView;
